import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import {
  ArrowUp,
  BarChart3,
  BookOpen,
  Building2,
  Check,
  Coins,
  ExternalLink,
  HelpCircle,
  Layout,
  Link2,
  Loader2,
  ReceiptText,
  Sparkles,
  User,
  Users,
  UserSearch,
  Wallet,
} from "lucide-react";
import { openWpSsoWindow } from "../lib/wp_sso";
import type { AuthUser } from "../lib/api";
import { navigate } from "../lib/router";
import { getCustomerDirectoryStatus, getDashboardSummary, getMyTeam, type AgentDashboardSummary, type TeamResponse } from "../lib/agent";
import { getPublicFeatures, type PublicFeatures } from "../lib/page";
import { useTour } from "../components/PortalTour";
import { OnboardingChecklist } from "../components/OnboardingChecklist";
import { HelpBox, InfoTip } from "../components/InfoTip";

type Props = {
  user: AuthUser;
};

function fmtEur(cents: number): string {
  return (cents / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
}

const TOOLS: Array<{
  group: string;
  items: Array<{
    icon: typeof Coins;
    title: string;
    description: string;
    href: string;
    disabled?: boolean;
  }>;
}> = [
  {
    group: "ÜBERSICHT",
    items: [
      {
        icon: Coins,
        title: "Provisionen",
        description: "Alle berechneten Provisionen — reserviert, freigegeben, ausgezahlt.",
        href: "/commissions",
      },
      {
        icon: Wallet,
        title: "Auszahlungen",
        description: "Vergangene Auszahlungen + nächste geplante Periode.",
        href: "/payouts",
      },
      {
        icon: BarChart3,
        title: "Statistik",
        description: "Klicks, Conversion-Rate, Top-Produkte — was funktioniert.",
        href: "/stats",
      },
    ],
  },
  {
    group: "MEIN NETZWERK",
    items: [
      {
        icon: Link2,
        title: "Empfehlungs-Links",
        description: "Default-Link, Kampagnen-Codes, Produkt-spezifische Links erstellen.",
        href: "/links",
      },
      {
        icon: Users,
        title: "Mein Team",
        description: "Einladungs-Link, Team-Mitglieder, Prozente pro Mitglied festlegen (als Master).",
        href: "/team",
      },
      {
        icon: UserSearch,
        title: "Meine Kunden",
        description: "Alle Personen die du geworben hast — mit Bestell-Anzahl und Verdienst.",
        href: "/customers",
      },
      {
        icon: Layout,
        title: "Mein Shop",
        description: "Eigener Mini-Shop unter deiner Subdomain — Produkte, Theme, Hero-Slider.",
        href: "/page-editor",
      },
      {
        icon: Sparkles,
        title: "Sharing-Vorlagen",
        description: "Fertige Story-Bilder, WhatsApp-Texte und Mail-Signaturen — Copy & Paste.",
        href: "/vorlagen",
      },
      {
        icon: BookOpen,
        title: "Online-Prospekt",
        description: "Aktuelles Angebots-Prospekt mit deiner Kennung teilen — Aufrufe + Klicks live.",
        href: "/prospekte",
      },
      {
        icon: ReceiptText,
        title: "Rechnungs-Check",
        description: "Großhändler-Rechnung hochladen, Ersparnis live zeigen — dein Verkaufs-Werkzeug im Kiosk.",
        href: "/rechnungscheck",
      },
    ],
  },
  {
    group: "MEIN KONTO",
    items: [
      {
        icon: User,
        title: "Stammdaten + Passwort",
        description: "Eigene Daten ändern, Passwort neu setzen.",
        href: "/account",
      },
      {
        icon: ArrowUp,
        title: "Auszahlungsdaten",
        description: "IBAN + Steuer-Daten hinterlegen — nötig, damit wir dich auszahlen können.",
        href: "/upgrade",
      },
    ],
  },
];

export function AgentHub({ user }: Props) {
  const firstName = user.name.split(" ")[0] || "Agent";

  const { data: summary } = useQuery<AgentDashboardSummary>({
    queryKey: ["agent-dashboard-summary"],
    queryFn: () => getDashboardSummary(),
    enabled: user.role === "agent",
  });

  // Feature-Flag: solange nicht geladen oder deaktiviert → "Mein Shop" ausgegraut
  const { data: features } = useQuery<PublicFeatures>({
    queryKey: ["public-features"],
    queryFn: () => getPublicFeatures(),
    staleTime: 60_000,
  });
  const shopEnabled = features?.agent_shop_enabled === true;
  const vorlagenEnabled = features?.sharing_templates_enabled !== false;

  // Team-Info: Mitglieder sehen prominent ihren Anteil am Master-Pool
  const { data: team } = useQuery<TeamResponse>({
    queryKey: ["agent-team"],
    queryFn: () => getMyTeam(),
    enabled: user.role === "agent",
    staleTime: 60_000,
  });

  // Kunden-Verzeichnis: Kachel nur zeigen, wenn der Admin es freigeschaltet hat
  const { data: dirStatus } = useQuery<{ enabled: boolean }>({
    queryKey: ["customer-directory-status"],
    queryFn: () => getCustomerDirectoryStatus(),
    enabled: user.role === "agent",
    staleTime: 300_000,
  });
  const toolGroups = dirStatus?.enabled
    ? TOOLS.map((g) =>
        g.group === "MEIN NETZWERK"
          ? {
              ...g,
              items: [
                ...g.items,
                {
                  icon: Building2,
                  title: "Kunden-Verzeichnis",
                  description:
                    "Alle bestehenden DP-Connect-Kunden — Name, Firma, Stadt. Filterbar nach Stadt.",
                  href: "/kundenliste",
                },
              ],
            }
          : g,
      )
    : TOOLS;

  return (
    <main className="mx-auto w-full max-w-6xl gradient-radial-warm px-4 py-8">
      {user.role === "agent" && (
        <div className="mb-3">
          <div className="flex justify-center md:justify-end gap-2">
            <WpSsoButton />
            <button
              onClick={() => navigate("/hilfe")}
              className="inline-flex items-center gap-1 whitespace-nowrap rounded-lg border border-line bg-white/70 backdrop-blur-sm px-3 py-1.5 text-xs font-medium text-ink-soft hover:bg-white hover:text-ink"
            >
              <BookOpen className="size-3.5" /> Hilfe + Anleitung
            </button>
          </div>
          <div className="flex justify-center md:justify-end mt-1.5">
            <TourRestartButton />
          </div>
        </div>
      )}

      <div className="text-center mb-8">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight">
          Hi {firstName} — <span className="brand-text-gradient">deine Provision</span>
        </h2>
        {user.agent_code && (
          <p className="mt-2 text-sm text-ink-muted">
            Dein persönlicher Link-Name:{" "}
            <code className="font-mono font-medium">{user.agent_code}</code>
            <span className="text-ink-muted/70"> · steckt in deinen Links & deinem Shop</span>
          </p>
        )}
      </div>

      {/* Team-Mitglied: prominenter Hinweis auf den eigenen Anteil */}
      {user.role === "agent" && team && !team.is_master && (
        <div className="mb-6 mx-auto max-w-2xl">
          <button
            type="button"
            onClick={() => navigate("/team")}
            className="w-full rounded-2xl border border-orange-200 bg-orange-50/70 px-4 py-3 text-sm text-left hover:bg-orange-50 transition"
            title="Zu 'Mein Team'"
          >
            <span className="font-semibold text-orange-900">
              🤝 Team {team.master_first_name || "Master"}
            </span>
            {team.my_share_rate != null ? (
              <span className="text-orange-900">
                {" — du erhältst "}
                <strong>
                  {(Math.round(team.my_share_rate * 1000) / 10)
                    .toString()
                    .replace(".", ",")}{" "}
                  %
                </strong>{" "}
                von {team.master_first_name}s Provision auf Bestellungen deiner
                Kunden.
              </span>
            ) : (
              <span className="text-orange-900">
                {" — deinen Provisions-Anteil legt "}
                {team.master_first_name || "dein Master"} fest.
              </span>
            )}
          </button>
        </div>
      )}

      {user.role === "agent" && <OnboardingChecklist />}

      {user.role === "agent" && (
        <div className="mb-6">
          <HelpBox kind="info" collapsible title="📚 Wie funktioniert das Portal?">
            Dein Konto verdient auf <strong>3 Wegen</strong>:
            <br />• <strong>Eigene Verkäufe</strong> — alles was Kunden über
            deinen Link / deinen Shop kaufen → du bekommst deinen Anteil
            <br />• <strong>Dein Team</strong> (als Master) — Bestellungen der
            Kunden deiner Team-Mitglieder → du bekommst den Rest des Pools,
            nachdem das Mitglied seinen Satz erhalten hat
            <br />• <strong>Wiederkäufe deiner Kunden</strong> — wenn ein über
            dich angemeldeter Kunde später nochmal bestellt → du verdienst
            weiter
            <br />
            <br />
            Provisionen durchlaufen <strong>3 Phasen</strong>: Reserviert (nach
            Bestellung) → Freigegeben (nach Widerrufsfrist) → Ausgezahlt (am
            Auszahlungs-Termin).
          </HelpBox>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-10">
        <SummaryCard
          label="Reserviert"
          amount={summary ? fmtEur(summary.reserved_total_cents) : "—,— €"}
          hint="Wird nach Reserve-Periode freigegeben"
          tooltip="Provisionen aus frischen Bestellungen — werden nach Ablauf der Widerrufsfrist (i.d.R. 14 Tage) automatisch freigegeben. Falls der Kunde widerruft, fällt der Betrag wieder weg."
        />
        <SummaryCard
          label="Freigegeben"
          amount={summary ? fmtEur(summary.approved_total_cents) : "—,— €"}
          hint="Wartet auf nächste Auszahlung"
          tooltip="Provisionen die sicher dir gehören — werden am nächsten Auszahlungs-Termin (monatlich) auf dein Konto überwiesen."
        />
        <SummaryCard
          label="Letzte 30 Tage"
          amount={summary ? fmtEur(summary.last_30_days_cents) : "—,— €"}
          hint={
            summary?.last_order_date
              ? `Letzte Bestellung: ${new Date(summary.last_order_date).toLocaleDateString("de-DE")}`
              : "Noch keine Bestellung"
          }
          tooltip="Summe aller Provisionen aus Bestellungen der letzten 30 Tage — egal ob noch reserviert, freigegeben oder ausgezahlt."
        />
      </div>

      <div className="space-y-8">
        {toolGroups.map((group) => (
          <section key={group.group}>
            <div className="mb-3 px-1">
              <h3 className="text-xs font-semibold tracking-wider text-ink-muted">{group.group}</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {group.items.map((tool) => {
                const Icon = tool.icon;
                const isDisabled =
                  tool.disabled ||
                  (tool.href === "/page-editor" && !shopEnabled) ||
                  (tool.href === "/vorlagen" && !vorlagenEnabled);
                if (isDisabled) {
                  return (
                    <div
                      key={tool.href}
                      className="rounded-xl border border-line bg-white/70 backdrop-blur-sm p-3 opacity-60 cursor-not-allowed relative"
                    >
                      <span className="absolute right-2 top-2 rounded-full bg-amber-100 text-amber-900 text-[10px] font-medium px-2 py-0.5">
                        BALD
                      </span>
                      <div className="flex items-start gap-3">
                        <div className="grid size-8 place-items-center rounded-lg bg-cream-soft text-ink-muted">
                          <Icon className="size-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4 className="text-sm font-semibold text-ink-soft">{tool.title}</h4>
                          <p className="mt-0.5 text-xs text-ink-muted leading-snug">
                            {tool.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                }
                return (
                  <a
                    key={tool.href}
                    href={tool.href}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(tool.href);
                    }}
                    className="rounded-xl border border-line bg-white/70 backdrop-blur-sm p-3 hover:bg-white transition"
                  >
                    <div className="flex items-start gap-3">
                      <div className="grid size-8 place-items-center rounded-lg brand-gradient-soft text-accent-orange">
                        <Icon className="size-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h4 className="text-sm font-semibold">{tool.title}</h4>
                        <p className="mt-0.5 text-xs text-ink-muted leading-snug">
                          {tool.description}
                        </p>
                      </div>
                    </div>
                  </a>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </main>
  );
}

function SummaryCard({
  label,
  amount,
  hint,
  tooltip,
}: {
  label: string;
  amount: string;
  hint: string;
  tooltip?: string;
}) {
  return (
    <div className="rounded-2xl border border-line bg-white p-4">
      <div className="text-xs uppercase tracking-wide font-semibold text-ink-muted flex items-center gap-1.5">
        {label}
        {tooltip && <InfoTip title={label}>{tooltip}</InfoTip>}
      </div>
      <div className="mt-2 text-2xl font-semibold tracking-tight font-mono">{amount}</div>
      <div className="mt-1 text-xs text-ink-muted">{hint}</div>
    </div>
  );
}

function WpSsoButton() {
  const [state, setState] = useState<"idle" | "busy" | "ok" | "err">("idle");
  const [msg, setMsg] = useState<string>("");
  const trigger = async () => {
    setState("busy");
    setMsg("");
    // Top-Level-Navigation in neuem Tab — funktioniert auch in Safari,
    // wo der stille fetch-SSO an ITP scheitert (Cookies werden verworfen).
    const res = await openWpSsoWindow();
    if (res.status === "ok") {
      setState("ok");
      setMsg("dpconnect.de geöffnet — eingeloggt + Teilen-Button aktiv");
      window.setTimeout(() => setState("idle"), 4000);
    } else if (res.status === "skipped") {
      setState("err");
      setMsg(
        res.reason === "no_wc_user_linked"
          ? "Dein Konto ist noch nicht mit dpconnect.de verknüpft"
          : `Skip: ${res.reason}`,
      );
      window.setTimeout(() => setState("idle"), 4000);
    } else {
      setState("err");
      setMsg(`Fehler: ${res.reason}`);
      window.setTimeout(() => setState("idle"), 4000);
    }
  };
  return (
    <div className="relative">
      <button
        onClick={trigger}
        disabled={state === "busy"}
        className="inline-flex items-center gap-1 whitespace-nowrap rounded-lg border border-line bg-white/70 backdrop-blur-sm px-3 py-1.5 text-xs font-medium text-ink-soft hover:bg-white hover:text-ink disabled:opacity-50"
        title="Mich auch auf dpconnect.de einloggen"
      >
        {state === "busy" ? (
          <Loader2 className="size-3.5 animate-spin" />
        ) : state === "ok" ? (
          <Check className="size-3.5 text-emerald-600" />
        ) : (
          <ExternalLink className="size-3.5" />
        )}
        {state === "ok" ? "Eingeloggt" : "Auf dpconnect.de einloggen"}
      </button>
      {msg && state !== "idle" && state !== "busy" && (
        <div
          className={`absolute right-0 top-full mt-1 whitespace-nowrap rounded-md px-2 py-1 text-[10px] shadow-md ${
            state === "ok"
              ? "bg-emerald-50 text-emerald-900 border border-emerald-200"
              : "bg-amber-50 text-amber-900 border border-amber-200"
          }`}
        >
          {msg}
        </div>
      )}
    </div>
  );
}

function TourRestartButton() {
  const tour = useTour();
  return (
    <button
      onClick={() => tour.start()}
      className="inline-flex items-center gap-1 whitespace-nowrap px-2 py-1 text-xs font-medium text-ink-muted hover:text-ink hover:underline"
    >
      <HelpCircle className="size-3.5" /> Tour erneut starten
    </button>
  );
}
